import { atom, type Atom } from 'jotai';
import { isEqual, omit } from 'lodash-es';
import searchAtomPair, {
  type SearchAtom,
  type SearchAtomOptions,
} from 'atoms/search-atom';
import request from 'lib/request';
import type {
  SearchResult,
  ApiQueryResource,
  ApiQueryResponse,
} from 'types/api-responses';
import type { MapRef } from 'react-map-gl';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import {
  WELL_STATUS_COLORS,
  WELL_STATUS_LABELS,
  type WellStatusToken,
} from 'types/well-workspace';
import type {
  DatasetConfig,
  DatasetSearchParams,
  DatasetFacets,
  SpatialFilterMode,
} from './types';
import { datasetRegistry } from './datasets';
import { DEFAULT_AREA_TYPE_RULE } from './datasets/areas-utils';

// ─── Per-Dataset Atom Bundle ───

type SearchAtomPairResult = ReturnType<
  typeof searchAtomPair<ApiQueryResponse, DatasetSearchParams, DatasetFacets>
>;

/** Write-only atom that refreshes facet counts. Setter takes current form values. */
type RefreshFacetsAtom = ReturnType<
  typeof atom<null, [Record<string, unknown>], void>
>;

export interface DatasetAtoms {
  searchAtom: SearchAtomPairResult['searchAtom'];
  resetSearchDataAtom: SearchAtomPairResult['resetSearchDataAtom'];
  // The applied search: the dataset's defaults until its first search goes out.
  appliedPayloadAtom: Atom<DatasetSearchParams>;
  pendingFiltersAtom: ReturnType<typeof atom<Partial<DatasetSearchParams>>>;
  // Filter values that are staged but disabled: kept out of the committed search
  // payload (so they don't affect results) while retained so they can be
  // re-enabled. Keyed by param, mirroring the search payload shape.
  disabledFiltersAtom: ReturnType<typeof atom<Partial<DatasetSearchParams>>>;
  filterModalOpenAtom: ReturnType<typeof atom<boolean>>;
  filterModalCategoryAtom: ReturnType<typeof atom<string>>;
  modalFacetsAtom: ReturnType<typeof atom<DatasetFacets | null>>;
  // True while a modal facet refresh is in flight — the form shows a loader
  // instead of the "run a search first" empty hint until the first counts land.
  modalFacetsLoadingAtom: ReturnType<typeof atom<boolean>>;
  // True once the modal's latest facet refresh fails; false again once counts
  // land or the staged counts are cleared, so a retry keeps its notice up.
  modalFacetsFailedAtom: Atom<boolean>;
  refreshFacetsAtom: RefreshFacetsAtom;
  // Sends the modal's last facet refresh again: its typeahead text over the
  // filters staged now.
  retryModalFacetsAtom: ReturnType<typeof atom<null, [], void>>;
  // Drops the staged counts, and any fetch of them still in flight, so the
  // options read the committed facets again.
  clearModalFacetsAtom: ReturnType<typeof atom<null, [], void>>;
  // Facets for the committed (applied) search — the single source the sidebar,
  // color-by, and selected-filter chips read. For datasets whose main search
  // carries facets inline it derives from the search response; for datasets
  // that opt out (facetsMode: 'separate', e.g. Documents) it holds the result
  // of a dedicated facets request fired by refreshCommittedFacetsAtom.
  committedFacetsAtom: Atom<DatasetFacets | null>;
  // True once the latest dedicated facets request ('separate' datasets) fails,
  // leaving the committed facets stale; false again once counts land or are
  // cleared, so a retry keeps its notice up.
  committedFacetsFailedAtom: Atom<boolean>;
  // True while a dedicated facets request is in flight.
  committedFacetsLoadingAtom: Atom<boolean>;
  refreshCommittedFacetsAtom: ReturnType<typeof atom<null, [], void>>;
  // The committed wells search's totalDuplicateCount (wells with potential
  // duplicates) — null for datasets whose response has no such field. Read by
  // the Potential Duplicates checkbox to show a count beside its label, the
  // same figure as the wells-listing banner.
  committedDuplicateCountAtom: Atom<number | null>;
  // Drops the committed counts, and any fetch of them still in flight, so a
  // late reply can't land once the page is gone.
  clearCommittedFacetsAtom: ReturnType<typeof atom<null, [], void>>;
  scopedLayerVisibleAtom: ReturnType<typeof atom<boolean>>;
  // The sidebar's eye: hides the dataset on the map only. Its table, counts and
  // searches carry on; scopedLayerVisibleAtom (the Datasets modal) hides it everywhere.
  mapLayerVisibleAtom: ReturnType<typeof atom<boolean>>;
  // Whether the dataset draws on the map: shown, and its eye open.
  onMapAtom: Atom<boolean>;
  layerColorAtom: ReturnType<typeof atom<string>>;
  // Read-only view of the global color rules scoped to this dataset (own rules
  // plus "common" rules). Rules are edited through mapQueryColorRulesAtom.
  colorRulesAtom: Atom<ColorByRule[]>;
  highlightedItemIdAtom: ReturnType<typeof atom<string | null>>;
  highlightedItemBoundsAtom: ReturnType<
    typeof atom<[number, number, number, number] | null>
  >;
  datasetTextAtom: ReturnType<typeof atom<string>>;
  // A row the app already holds that the search should list though the index
  // hasn't caught up — a just-created area, from its create response. Kept while
  // the search asks what it was pinned under, wherever the map has moved; any
  // other search (text, filters, page) clears it.
  pinnedRowAtom: ReturnType<typeof atom<PinnedRow | null>>;
}

/** A row pinned into one search: the row, and the payload of that search. */
export interface PinnedRow {
  row: ApiQueryResponse;
  payload: DatasetSearchParams | undefined;
}

// The wells the map shows start coloured by status (UAT): one rule over every status
// token, in the mock's status palette, painting the map and the table accents. The
// tokens are uppercase because that is how Elasticsearch indexes `status` and how the
// wells tiles and facets carry it — the rule matches them literally.
const WELL_STATUS_TOKENS = Object.keys(WELL_STATUS_LABELS) as WellStatusToken[];
export const DEFAULT_WELL_STATUS_RULE: ColorByRule = {
  ruleId: 'default-wells-status',
  attribute: 'status',
  selectedValues: WELL_STATUS_TOKENS.map((token) => token.toUpperCase()),
  selectedValueLabels: WELL_STATUS_TOKENS.map(
    (token) => WELL_STATUS_LABELS[token]
  ),
  valueColors: Object.fromEntries(
    WELL_STATUS_TOKENS.map((token) => [
      token.toUpperCase(),
      WELL_STATUS_COLORS[token],
    ])
  ),
  applyTo: { map: true, table: true },
  enabled: true,
  groupId: 'wells',
};

// Master color-by rule list shared across the tabbed Color-by-Attribute modal.
// Each rule's groupId marks the tab it was created under ('common' | dataset
// id); per-dataset colorRulesAtom reads a filtered slice of this. Seeded with the
// default status rule above and Areas' Area Type rule; a reader can edit or remove
// either like any other.
export const mapQueryColorRulesAtom = atom<ColorByRule[]>([
  DEFAULT_WELL_STATUS_RULE,
  DEFAULT_AREA_TYPE_RULE,
]);

// Color-by modal open state + edit target, lifted here so any rule legend (the
// global Common list or a per-dataset list) can open the single modal to edit a
// specific rule.
export const colorByModalOpenAtom = atom(false);
export const colorByEditingRuleIdAtom = atom<string | null>(null);
// The tab a new rule starts on (e.g. Subsurface Maps' Edit pencil); null = first.
export const colorByInitialGroupIdAtom = atom<string | null>(null);

/**
 * A results row's id, as a string; results rows are typed per dataset.
 *
 * @param {ApiQueryResponse} row - A results row.
 * @return {string} Its id.
 */
const rowId = (row: ApiQueryResponse) => String((row as { id?: unknown }).id);

/**
 * Whether two searches ask the same thing, the map's visible extent aside: the
 * extent follows the map as it pans or resizes (the create panel closing does),
 * while the text and filters are what the user chose.
 *
 * @param {DatasetSearchParams | undefined} a - One payload.
 * @param {DatasetSearchParams | undefined} b - The other.
 * @return {boolean} True when only the extent differs, if anything.
 */
const sameSearchAside = (
  a: DatasetSearchParams | undefined,
  b: DatasetSearchParams | undefined
) => isEqual(omit(a, 'bounding_box'), omit(b, 'bounding_box'));

/**
 * A search with a pinned row leading its results, while the search still asks
 * what it was pinned under (the map may have moved) and its results don't hold
 * the row yet.
 *
 * @param {SearchAtom} search - The fetched search.
 * @param {PinnedRow | null} pinned - The row to keep listed, if any.
 * @return {SearchAtom} The search as readers see it.
 */
export const withPinnedRow = (
  search: SearchAtom<ApiQueryResponse, DatasetSearchParams, DatasetFacets>,
  pinned: PinnedRow | null
): SearchAtom<ApiQueryResponse, DatasetSearchParams, DatasetFacets> => {
  const { data } = search;
  if (
    !pinned ||
    !data ||
    !sameSearchAside(search.currentPayload, pinned.payload) ||
    data.results.some((row) => rowId(row) === rowId(pinned.row))
  ) {
    return search;
  }
  return {
    ...search,
    data: {
      ...data,
      results: [pinned.row, ...data.results],
      totalCount: data.totalCount + 1,
    },
  };
};

/**
 * A search's counts request: its filters without paging or sort, with facets
 * and without the extent the list call carries. One row: a pageSize under 1
 * coerces up to max_records and times out.
 * @param {DatasetConfig} config The dataset.
 * @param {Record<string, unknown>} base The search to count.
 * @return {Record<string, unknown>} The request.
 */
const facetsRequest = (
  config: DatasetConfig,
  base: Record<string, unknown>
): Record<string, unknown> => ({
  ...omit(base, RESULTS_VIEW_KEYS),
  ...config.facetsPayload,
  page: 1,
  pageSize: 1,
  response_config: {
    ...config.facetsPayload?.response_config,
    include_facets: true,
    include_bounding_box: false,
  },
});

/**
 * Sends a facets request, aborting the one before it (tracked in abortRef), and
 * hands any facets to onFacets.
 * @param {string} endpoint The dataset's search endpoint.
 * @param {Record<string, unknown>} payload The facets request.
 * @param {object} abortRef Holds the request in flight, superseded by the next.
 * @param {object} listeners The request's listeners.
 * @param {Function} listeners.onFacets Receives the facets.
 * @param {Function} [listeners.onLoading] Told when the request starts and settles.
 * @param {Function} [listeners.onFailed] Told when the current request fails, not aborts.
 */
const fetchFacets = (
  endpoint: string,
  payload: Record<string, unknown>,
  abortRef: { current: AbortController | null },
  {
    onFacets,
    onLoading,
    onFailed,
  }: {
    onFacets: (facets: DatasetFacets) => void;
    onLoading?: (loading: boolean) => void;
    onFailed?: () => void;
  }
) => {
  abortRef.current?.abort();
  const controller = new AbortController();
  abortRef.current = controller;
  onLoading?.(true);
  request
    .post<SearchResult<ApiQueryResponse, DatasetFacets>, unknown>(
      endpoint,
      payload,
      controller
    )
    .then((response) => {
      // A superseded or cleared fetch must not land over newer counts.
      if (abortRef.current !== controller) return;
      // An answer without counts fails too, or the last failure would stay up.
      if (response.type === 'success' && response.data?.facets) {
        onFacets(response.data.facets);
      } else if (response.type !== 'abort') {
        onFailed?.();
      }
    })
    .catch(() => {
      // A failed request leaves the last-loaded counts in place.
      if (abortRef.current === controller) onFailed?.();
    })
    .finally(() => {
      // A superseded (aborted) call leaves loading set — its replacement is
      // now the current controller and still in flight to clear it.
      if (abortRef.current === controller) onLoading?.(false);
    });
};

/**
 * Builds one dataset's atom bundle: its search, staged and disabled filters,
 * facet counts, layer state and text.
 * @param {DatasetConfig} config The dataset.
 * @param {SearchAtomOptions} [options] How its search starts.
 * @return {DatasetAtoms} The atoms.
 */
export function createDatasetAtoms(
  config: DatasetConfig,
  options?: SearchAtomOptions
): DatasetAtoms {
  // Named resources (Wells) resolve their search href via the root resources
  // index (resources.queries[id].href). Datasets not in the index pass an
  // explicit { href } to bypass the lookup.
  const resource: ApiQueryResource | { href: string } = config.directHref
    ? { href: config.directHref }
    : (config.id as ApiQueryResource);
  const { searchAtom: fetchedSearchAtom, resetSearchDataAtom } = searchAtomPair<
    ApiQueryResponse,
    DatasetSearchParams,
    DatasetFacets
  >(resource, config.defaultPayload, options);

  const appliedPayloadAtom = atom(
    (get) => get(fetchedSearchAtom).currentPayload ?? config.defaultPayload
  );

  const pinnedRowAtom = atom<PinnedRow | null>(null);
  // The search as every reader sees it: the fetched results, with a pinned row
  // leading them until the index returns it or the text or filters change. Writes run the search as before; the first
  // search still runs as the fetched atom mounts under this one.
  const searchAtom: SearchAtomPairResult['searchAtom'] = atom(
    (get) => withPinnedRow(get(fetchedSearchAtom), get(pinnedRowAtom)),
    (get, set, payload) => {
      set(fetchedSearchAtom, payload);
      // The search records its payload before it fetches, so a pin left behind
      // by a search that now asks something else is dropped here, not kept for
      // whenever the same search comes round again.
      const pinned = get(pinnedRowAtom);
      if (
        pinned &&
        !sameSearchAside(get(fetchedSearchAtom).currentPayload, pinned.payload)
      ) {
        set(pinnedRowAtom, null);
      }
      return undefined;
    }
  );

  const pendingFiltersAtom = atom<Partial<DatasetSearchParams>>({});
  const disabledFiltersAtom = atom<Partial<DatasetSearchParams>>({});
  const modalFacetsAtom = atom<DatasetFacets | null>(null);
  const modalFacetsLoadingAtom = atom(false);
  const modalFacetsFailedAtom = atom(false);

  const facetRef: { current: AbortController | null } = { current: null };
  const committedFacetRef: { current: AbortController | null } = {
    current: null,
  };

  // Applied text-search params (e.g. current_operator, lease_name) are real
  // filters that would collapse every facet count, so they're dropped from the
  // facet query — they apply only to the committed search on "Apply Filters".
  const textParamKeys = new Set(
    config.filters
      .filter((f) => 'textParam' in f)
      .map((f) => (f as { textParam: string }).textParam)
  );

  // The typeahead text (the form's facet_search_* keys) the modal last
  // refreshed with. A retry sends it again over the filters staged by then.
  const lastFacetSearchAtom = atom<Record<string, unknown>>({});

  const refreshFacetsAtom = atom(
    null,
    (get, set, formValues: Record<string, unknown>) => {
      set(
        lastFacetSearchAtom,
        Object.fromEntries(
          Object.entries(formValues).filter(([key]) =>
            key.startsWith('facet_search_')
          )
        )
      );
      const payload = facetsRequest(config, {
        ...config.defaultPayload,
        ...get(pendingFiltersAtom),
        ...formValues,
      });
      for (const key of textParamKeys) delete payload[key];
      fetchFacets(config.searchEndpoint, payload, facetRef, {
        onFacets: (facets) => {
          set(modalFacetsAtom, facets);
          set(modalFacetsFailedAtom, false);
        },
        onLoading: (loading) => set(modalFacetsLoadingAtom, loading),
        onFailed: () => set(modalFacetsFailedAtom, true),
      });
    }
  ) as RefreshFacetsAtom;

  const retryModalFacetsAtom = atom(null, (get, set) =>
    set(refreshFacetsAtom, get(lastFacetSearchAtom))
  );

  const clearModalFacetsAtom = atom(null, (_get, set) => {
    facetRef.current?.abort();
    facetRef.current = null;
    set(modalFacetsAtom, null);
    set(modalFacetsLoadingAtom, false);
    set(modalFacetsFailedAtom, false);
  });

  // Facets for the committed search. Inline datasets derive them from the main
  // search response; 'separate' datasets keep the last dedicated-fetch result
  // here (see refreshCommittedFacetsAtom) so the map/list search stays facet-free.
  const separateFacets = config.facetsMode === 'separate';
  const rawCommittedFacetsAtom = atom<DatasetFacets | null>(null);
  const committedFacetsFailedAtom = atom(false);
  const committedFacetsLoadingAtom = atom(false);
  const committedFacetsAtom = atom((get) =>
    separateFacets
      ? get(rawCommittedFacetsAtom)
      : (get(searchAtom).data?.facets ?? null)
  );

  // Write-only: fetch facets for the CURRENT committed payload, off the critical
  // path, so panning/filtering doesn't wait on facet resolution. Superseded
  // calls abort. No-op for inline datasets (their facets ride the main search).
  const refreshCommittedFacetsAtom = atom(null, (get, set) => {
    if (!separateFacets) return;
    const payload = get(searchAtom).currentPayload;
    if (!payload) return;
    fetchFacets(
      config.searchEndpoint,
      facetsRequest(config, payload),
      committedFacetRef,
      {
        onFacets: (facets) => {
          set(rawCommittedFacetsAtom, facets);
          set(committedFacetsFailedAtom, false);
        },
        onLoading: (loading) => set(committedFacetsLoadingAtom, loading),
        onFailed: () => set(committedFacetsFailedAtom, true),
      }
    );
  });

  // The committed search's totalDuplicateCount, read off the main search response
  // (null for datasets without the field). Mirrors committedFacetsAtom's inline derivation.
  const committedDuplicateCountAtom = atom(
    (get) => get(searchAtom).data?.totalDuplicateCount ?? null
  );

  const clearCommittedFacetsAtom = atom(null, (_get, set) => {
    committedFacetRef.current?.abort();
    committedFacetRef.current = null;
    set(rawCommittedFacetsAtom, null);
    set(committedFacetsLoadingAtom, false);
    set(committedFacetsFailedAtom, false);
  });

  const scopedLayerVisibleAtom = atom(true);
  const mapLayerVisibleAtom = atom(true);

  return {
    searchAtom,
    resetSearchDataAtom,
    appliedPayloadAtom,
    pendingFiltersAtom,
    disabledFiltersAtom,
    filterModalOpenAtom: atom(false),
    filterModalCategoryAtom: atom(
      config.defaultFilterCategory ?? config.filters[0]?.category ?? ''
    ),
    modalFacetsAtom,
    modalFacetsLoadingAtom,
    modalFacetsFailedAtom,
    refreshFacetsAtom,
    retryModalFacetsAtom,
    clearModalFacetsAtom,
    committedFacetsAtom,
    committedFacetsFailedAtom,
    committedFacetsLoadingAtom,
    refreshCommittedFacetsAtom,
    committedDuplicateCountAtom,
    clearCommittedFacetsAtom,
    scopedLayerVisibleAtom,
    mapLayerVisibleAtom,
    onMapAtom: atom(
      (get) => get(scopedLayerVisibleAtom) && get(mapLayerVisibleAtom)
    ),
    layerColorAtom: atom(config.defaultLayerColor ?? '#000000'),
    colorRulesAtom: atom((get) =>
      get(mapQueryColorRulesAtom).filter(
        (r) => r.groupId === config.id || r.groupId === 'common'
      )
    ),
    highlightedItemIdAtom: atom<string | null>(null),
    highlightedItemBoundsAtom: atom<[number, number, number, number] | null>(
      null
    ),
    datasetTextAtom: atom(''),
    pinnedRowAtom,
  };
}

// Pre-build atom bundles for every registered dataset. While the lists wait for
// the map's first bounds, those start each search; otherwise mounting does.
export const allDatasetAtoms: Record<string, DatasetAtoms> = Object.fromEntries(
  Object.entries(datasetRegistry).map(([id, config]) => [
    id,
    createDatasetAtoms(config, {
      searchOnMount: (get) => !get(waitingForMapBoundsAtom),
    }),
  ])
);

// Starts each list that has yet to search on its defaults, for when the map's
// first bounds won't come. A list already searched keeps its filters.
export const runDefaultSearchesAtom = atom(null, (get, set) => {
  for (const { searchAtom } of Object.values(allDatasetAtoms)) {
    if (!get(searchAtom).currentPayload) set(searchAtom, undefined);
  }
});

// ─── Shared Atoms (not per-dataset) ───

export const mapSearchRefAtom = atom<MapRef | null>(null);

// eventData a programmatic flyTo carries so visible-extent mode's moveend
// handler skips the rebound search that would drop the just-selected card.
// Shared so the write (results-section) and read (spatial-filter-controls) ends
// can't drift on a rename.
export type MapMoveMeta = { suppressBoundsSearch?: boolean };
export const SUPPRESS_BOUNDS_SEARCH: MapMoveMeta = {
  suppressBoundsSearch: true,
};

export const spatialFilterModeAtom = atom<SpatialFilterMode>('visible-extent');
// Documents-only: plot/match documents by their recording county rather than
// their survey-area centroid. Written into the documents search payload as
// `plot_by_county` (see the sidebar's Plot-by control). Only meaningful while a
// spatial map filter is active.
export const plotByCountyAtom = atom(false);
export const aoiPolygonAtom = atom<GeoJSON.Polygon | null>(null);
export const aoiPolygonLockedAtom = atom(true);
export const dividerPositionAtom = atom(50);
export const sidebarCollapsedAtom = atom(false);
export const showMapAtom = atom(true);
// The lists wait for the map's first bounds: only the sidebar's spatial control,
// with the map shown, sends them in visible-extent mode.
export const waitingForMapBoundsAtom = atom(
  (get) =>
    get(spatialFilterModeAtom) === 'visible-extent' &&
    get(showMapAtom) &&
    !get(sidebarCollapsedAtom)
);
export const showTableAtom = atom(true);

// Global text search (shared across datasets)
export const globalTextAtom = atom('');

// Which dataset tab is active in the results section
const datasetIds = Object.keys(datasetRegistry);
export const activeDatasetTabAtom = atom<string>(datasetIds[0] ?? '');

// Ids of datasets currently toggled visible in the Datasets modal. Derived so
// consumers subscribe to one atom instead of looping useAtomValue per dataset.
export const visibleDatasetIdsAtom = atom((get) =>
  datasetIds.filter((id) => get(allDatasetAtoms[id].scopedLayerVisibleAtom))
);

// Per-dataset export snapshot: the live filtered count + the committed search payload for every
// dataset, in one derived atom. The spatial-export roster reads this so it can be built generically
// from datasetRegistry (one useAtomValue) rather than reading each dataset's searchAtom with a fixed
// hook call — a new exportable dataset then joins the roster with no change here (AC 9).
export const datasetExportSnapshotAtom = atom(
  (get) =>
    Object.fromEntries(
      Object.entries(allDatasetAtoms).map(([id, atoms]) => {
        const search = get(atoms.searchAtom);
        return [
          id,
          {
            count: search.data?.totalCount,
            payload: get(atoms.appliedPayloadAtom),
          },
        ];
      })
    ) as Record<string, { count?: number; payload: Record<string, unknown> }>
);

// ─── Active-Filter Counting ───

// Paging and ordering: they change which matching rows show, never which rows
// match, so no filter count, facet or filter commit reads them.
export const RESULTS_VIEW_KEYS = [
  'page',
  'pageSize',
  'sortAttribute',
  'sortDirection',
  'paginated',
];

// Payload keys that aren't user filters (pagination, sort, text, spatial).
const FILTER_IGNORE_KEYS = new Set([
  ...RESULTS_VIEW_KEYS,
  'use_elastic',
  'text',
  // Response-shaping directive (facet inclusion), not a user filter — some
  // datasets carry it in their default payload, so it must not inflate the count.
  'response_config',
  // A response opt-in (area-scoped duplicate count), not a user filter — the wells
  // dataset carries it in its default payload, so it must not inflate the count.
  'include_scoped_duplicate_count',
  'bounding_box',
  'polygon_geojson',
  // A plotting mode tied to the spatial filter, not an independent filter — and
  // it has no filter-modal chip, so it must not inflate the applied-filter count.
  'plot_by_county',
  // Which fields the text search matches; a default, never a user filter.
  'search_scope',
]);

/**
 * Whether a payload value is a set filter (not empty, blank or an empty list).
 * @param {unknown} value The value.
 * @return {boolean} Whether it is set.
 */
const isSet = (value: unknown) =>
  value !== undefined &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

/**
 * Counts the non-default filter keys in a search payload. A value range is one
 * filter (one chip), so its max isn't counted again when its min is set.
 * @param {Record<string, unknown> | undefined} payload The search payload.
 * @param {DatasetConfig} [config] The dataset, for its value-histogram fields.
 * @return {number} The number of active filters.
 */
export function countActiveFilters(
  payload: Record<string, unknown> | undefined,
  config?: DatasetConfig
): number {
  if (!payload) return 0;
  const minOfMax = new Map<string, string>();
  config?.filters.forEach((field) => {
    if (field.type === 'value-histogram')
      minOfMax.set(field.maxParam, field.minParam);
  });
  return Object.entries(payload).filter(([key, value]) => {
    if (FILTER_IGNORE_KEYS.has(key) || !isSet(value)) return false;
    const minParam = minOfMax.get(key);
    return !(minParam && isSet(payload[minParam]));
  }).length;
}

// Sum of active filters across every dataset — backs "Map Query (N filters applied)".
export const totalActiveFiltersAtom = atom((get) =>
  Object.entries(allDatasetAtoms).reduce(
    (sum, [id, atoms]) =>
      sum +
      countActiveFilters(get(atoms.appliedPayloadAtom), datasetRegistry[id]),
    0
  )
);

/**
 * Asks for the results' extent whenever no bounding box scopes the search; the
 * map fits to it in No Map Filter and AOI. A dataset whose defaults never ask
 * for the extent is left alone.
 * @param {DatasetSearchParams} payload The next search.
 * @param {DatasetSearchParams} defaultPayload The dataset's default payload.
 * @return {DatasetSearchParams} The search, its flag set by the box.
 */
const withExtentFlag = (
  payload: DatasetSearchParams,
  defaultPayload: DatasetSearchParams
): DatasetSearchParams => {
  if (!defaultPayload.response_config?.include_bounding_box) return payload;
  return {
    ...payload,
    response_config: {
      ...defaultPayload.response_config,
      ...payload.response_config,
      include_bounding_box: !payload.bounding_box,
    },
  };
};

/**
 * Clears one dataset's filters back to its defaults, keeping what isn't one
 * (the extent or AOI, the text, the plot mode), with the extent flag set by the
 * box. The filter modal's Clear and Reset and "Reset all filters" share it.
 * @param {DatasetSearchParams} defaultPayload The dataset's default payload.
 * @param {Partial<DatasetSearchParams> | undefined} current The applied payload.
 * @return {DatasetSearchParams} The cleared payload.
 */
export function clearedFilters(
  defaultPayload: DatasetSearchParams,
  current: Partial<DatasetSearchParams> | undefined
): DatasetSearchParams {
  return withExtentFlag(
    {
      ...defaultPayload,
      bounding_box: current?.bounding_box,
      polygon_geojson: current?.polygon_geojson,
      plot_by_county: current?.plot_by_county,
      text: current?.text ?? defaultPayload.text,
    },
    defaultPayload
  );
}

// Resets every dataset's discrete filters back to defaults (Documents included),
// preserving each one's spatial extent/AOI, text, and plot mode via
// clearedFilters and leaving the active tab untouched. Backs the toolbar's
// "Reset all filters".
export const resetAllFiltersAtom = atom(null, (_get, set) => {
  for (const [id, atoms] of Object.entries(allDatasetAtoms)) {
    const defaultPayload = datasetRegistry[id].defaultPayload;
    set(atoms.searchAtom, (current) => clearedFilters(defaultPayload, current));
    // Drop staged/disabled selections too, so reopening the filter modal
    // doesn't show cleared filters as still-selected.
    set(atoms.pendingFiltersAtom, {});
    set(atoms.disabledFiltersAtom, {});
  }
});

// ─── Text Helpers ───

/**
 * Escapes the parens a box leaves unpaired, and a trailing backslash, so its
 * syntax stays inside its group. As in simple_query_string, parens pair
 * regardless of quotes and a backslash escapes the next char.
 * @param {string} text The box's text.
 * @return {string} The escaped text.
 */
function escapeUnpaired(text: string): string {
  const open: number[] = [];
  const unpaired: number[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\\') {
      if (i === text.length - 1) unpaired.push(i);
      i++;
    } else if (text[i] === '(') {
      open.push(i);
    } else if (text[i] === ')') {
      if (open.pop() === undefined) unpaired.push(i);
    }
  }
  const out = text.split('');
  for (const i of [...open, ...unpaired]) out[i] = `\\${out[i]}`;
  return out.join('');
}

/**
 * Joins the global and dataset search text. With `narrow`, both texts must
 * match (see DatasetConfig.narrowsGlobalText); otherwise they're space-joined.
 * @param {string} global The global search text.
 * @param {string} dataset The dataset's search text.
 * @param {boolean} [narrow] Require both to match.
 * @return {string | undefined} The search text, unset when empty.
 */
export function combineText(
  global: string,
  dataset: string,
  narrow?: boolean
): string | undefined {
  const parts = [global.trim(), dataset.trim()].filter(Boolean);
  if (narrow && parts.length === 2) {
    const [first, second] = parts.map(escapeUnpaired);
    return `(${first}) + (${second})`;
  }
  return parts.join(' ') || undefined;
}

// ─── Broadcast Atom ───

// The most recent spatial filter (map extent / drawn polygon) broadcast to the
// datasets. A hidden dataset skips the fetch once it has searched, so this lets
// it catch up to the current map filter when it is toggled back on.
type SpatialFilterFragment = Pick<
  DatasetSearchParams,
  'bounding_box' | 'polygon_geojson'
>;
export const lastSpatialFilterAtom = atom<SpatialFilterFragment>({});

// Applies an updater to every shown dataset's search, and to a hidden one until
// its first search goes out, or it would never load. Then a hidden one waits,
// even after a failed search, until it's shown (setDatasetVisibleAtom).
export const broadcastSearchUpdateAtom = atom(
  null,
  (
    get,
    set,
    updater: (current: DatasetSearchParams) => DatasetSearchParams | undefined
  ) => {
    let spatialCaptured = false;
    for (const [id, atoms] of Object.entries(allDatasetAtoms)) {
      const defaultPayload = datasetRegistry[id].defaultPayload;

      // Record the resulting spatial fields once (they're identical across
      // datasets) so a later re-enable can replay them.
      if (!spatialCaptured) {
        const next = updater(
          get(atoms.searchAtom).currentPayload ?? defaultPayload
        );
        if (next) {
          set(lastSpatialFilterAtom, {
            bounding_box: next.bounding_box,
            polygon_geojson: next.polygon_geojson,
          });
          spatialCaptured = true;
        }
      }

      if (
        get(atoms.scopedLayerVisibleAtom) ||
        !get(atoms.searchAtom).currentPayload
      ) {
        set(atoms.searchAtom, (current) => {
          const next = updater(current ?? defaultPayload);
          return next && withExtentFlag(next, defaultPayload);
        });
      }
    }
  }
);

// Toggles a dataset's map/results visibility. When enabling one that was
// hidden, it replays the current spatial filter so its results match the map
// the user is looking at (broadcasts skipped it while hidden).
export const setDatasetVisibleAtom = atom(
  null,
  (
    get,
    set,
    { datasetId, visible }: { datasetId: string; visible: boolean }
  ) => {
    const atoms = allDatasetAtoms[datasetId];
    if (!atoms) return;

    const wasVisible = get(atoms.scopedLayerVisibleAtom);
    set(atoms.scopedLayerVisibleAtom, visible);
    if (!visible || wasVisible) return;

    const spatial = get(lastSpatialFilterAtom);
    const defaultPayload = datasetRegistry[datasetId].defaultPayload;
    set(atoms.searchAtom, (current) =>
      withExtentFlag(
        { ...(current ?? defaultPayload), ...spatial, page: 1 },
        defaultPayload
      )
    );
  }
);

// ─── Text Search Atoms ───

// Sets global text and triggers search on ALL datasets with combined text
export const setGlobalTextAtom = atom(null, (get, set, value: string) => {
  set(globalTextAtom, value);
  for (const [id, atoms] of Object.entries(allDatasetAtoms)) {
    set(atoms.resetSearchDataAtom, undefined);
    const datasetText = get(atoms.datasetTextAtom);
    const { defaultPayload, narrowsGlobalText } = datasetRegistry[id];
    set(atoms.searchAtom, (current) => ({
      ...(current ?? defaultPayload),
      page: 1,
      text: combineText(value, datasetText, narrowsGlobalText),
    }));
  }
});

// Sets per-dataset text and triggers search on ONLY that dataset with combined text.
// The global box lives on the Map Query page, so it only narrows that page's datasets.
export const setDatasetTextAtom = atom(
  null,
  (
    get,
    set,
    {
      atoms,
      config,
      value,
    }: {
      atoms: DatasetAtoms;
      config: Pick<
        DatasetConfig,
        'defaultPayload' | 'narrowsGlobalText' | 'usesGlobalText'
      >;
      value: string;
    }
  ) => {
    set(atoms.datasetTextAtom, value);
    set(atoms.resetSearchDataAtom, undefined);
    const { defaultPayload, narrowsGlobalText, usesGlobalText } = config;
    const globalText = usesGlobalText ? get(globalTextAtom) : '';
    set(atoms.searchAtom, (current) => ({
      ...(current ?? defaultPayload),
      page: 1,
      text: combineText(globalText, value, narrowsGlobalText),
    }));
  }
);

// Which table the Wells dataset's results panel shows: the wells the map is filtered
// to, or the well workspaces (mock v11's "View" dropdown on the table toolbar). An atom
// rather than section state because the section unmounts with its tab, and a reader who
// switched to Workspaces should find it still selected when they come back.
export type WellsResultsView = 'wells' | 'workspaces';
export const wellsResultsViewAtom = atom<WellsResultsView>('wells');

/** Read in place of a dataset that is not registered, so a hook reads unconditionally. */
export const noFacetsAtom = atom<DatasetFacets | null>(null);
